"use client";

import { useEffect } from "react";
import { Link } from "./link";
import { HStack, VStack } from "./primitives";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { AlertCircle } from "lucide-react";
import * as Sentry from "@sentry/nextjs";

export function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const errorMessage =
    !error.message || error.message === "{}" ? "Error" : error.message;
  return (
    <div className="w-full h-full p-4 overflow-hidden">
      <div className="w-full h-full border-2 border-destructive rounded-sm p-4 bg-destructive/10">
        <VStack className="items-center justify-center h-full gap-6 max-w-md mx-auto">
          <Alert
            variant="destructive"
            className="max-w-md mx-auto bg-background"
          >
            <AlertCircle className="size-4" />
            <AlertTitle className="truncate">{errorMessage}</AlertTitle>
            <AlertDescription className="text-sm [hanging-punctuation:first] text-foreground space-y-3 pt-3">
              <p>Something isn&apos;t working as expected. Please try again.</p>
              <p>
                If you need help, please get in touch via the feedback button
                or&nbsp;
                <Link
                  href={`mailto:hello@getuserfeedback.com?subject=Error%20on%20${window.location.pathname}&body=Error%20message:%20${errorMessage}%0A%0AError%20digest:%20${error.digest}`}
                  className="text-sm text-destructive-foreground hover:text-darken-destructive-foreground/20 active:text-darken-destructive-foreground/40"
                >
                  via&nbsp;email.
                </Link>
              </p>
              <p>
                The team is notified when errors like this happen so we will
                look into this on our end as soon as possible.
              </p>
              <HStack className="justify-end items-center gap-2">
                {error.digest && (
                  <p className="flex-1 text-sm text-destructive-foreground/30 min-w-0 truncate">
                    Error digest: {error.digest}
                  </p>
                )}
                <Button onClick={() => reset()} variant="destructive">
                  Try again
                </Button>
              </HStack>
            </AlertDescription>
          </Alert>
        </VStack>
      </div>
    </div>
  );
}
